import React from "react";
import { Icon } from "./Icons";
import "./Confirmation.css";
import "./Therapists.css";

function Confirmation({ bookingData, therapist }) {
    const isPaid = bookingData.service.priceCents > 0;

    const formatDate = (date) =>
        date.toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    const generateCalendarLink = () => {
        try {
            const therapistText = therapist ? ` with ${therapist.name}` : "";
            const title = `Therapy Session${therapistText} — ${bookingData.service.name}`;
            const details = `Session with Harbor Therapy${therapistText}\\n\\nService: ${bookingData.service.name}\\nDuration: ${bookingData.service.duration}\\n\\nLocation: 142 Montague Street, Suite 4B, Brooklyn, NY 11201`;
            const location = "Harbor Therapy · 142 Montague St, Suite 4B, Brooklyn, NY";

            const startDate = new Date(bookingData.date);
            const timeMatch = bookingData.time.match(/(\d+):(\d+)\s*(AM|PM)/i);

            if (timeMatch) {
                const hours = parseInt(timeMatch[1]);
                const minutes = parseInt(timeMatch[2]);
                const isPM = timeMatch[3].toUpperCase() === "PM";
                startDate.setHours(
                    isPM && hours !== 12 ? hours + 12 : hours === 12 && !isPM ? 0 : hours
                );
                startDate.setMinutes(minutes);
            }

            const startStr =
                startDate.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

            return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startStr}/${startStr}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}`;
        } catch (error) {
            return "#";
        }
    };

    return (
        <div className="confirmation-container">
            <div className="confirmation-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                    <circle cx="40" cy="40" r="40" fill="#E8EFE9" />
                    <circle cx="40" cy="40" r="30" fill="#4A6B5C" />
                    <path
                        d="M30 40L36 46L50 32"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </div>

            <h2>You&rsquo;re all set.</h2>
            <p className="confirmation-subtitle">
                {isPaid
                    ? "Payment received and your appointment is confirmed."
                    : "Your appointment is confirmed."}
            </p>

            {therapist && (
                <div className="confirmation-therapist">
                    <div
                        className="confirmation-therapist-portrait"
                        style={{
                            background: `linear-gradient(135deg, ${therapist.portraitColors[0]}, ${therapist.portraitColors[1]})`,
                        }}
                    >
                        {therapist.name
                            .replace(/^(Dr\.|Mr\.|Ms\.|Mrs\.)\s+/, "")
                            .split(" ")
                            .map((p) => p[0])
                            .slice(0, 2)
                            .join("")}
                    </div>
                    <div className="confirmation-therapist-info">
                        <span className="confirmation-therapist-label">
                            You're meeting with
                        </span>
                        <span className="confirmation-therapist-name">
                            {therapist.name}, {therapist.credential}
                        </span>
                    </div>
                </div>
            )}

            <div className="confirmation-details">
                <div className="detail-card">
                    <Icon name="calendar" className="detail-svg-icon" size={22} />
                    <div className="detail-content">
                        <span className="detail-label">Date &amp; time</span>
                        <span className="detail-value">
                            {formatDate(bookingData.date)}
                            <br />
                            {bookingData.time}
                        </span>
                    </div>
                </div>

                <div className="detail-card">
                    <Icon name="clock" className="detail-svg-icon" size={22} />
                    <div className="detail-content">
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{bookingData.service.duration}</span>
                    </div>
                </div>

                <div className="detail-card">
                    <Icon name="briefcase" className="detail-svg-icon" size={22} />
                    <div className="detail-content">
                        <span className="detail-label">Service</span>
                        <span className="detail-value">{bookingData.service.name}</span>
                    </div>
                </div>

                <div className="detail-card">
                    <Icon name="user" className="detail-svg-icon" size={22} />
                    <div className="detail-content">
                        <span className="detail-label">Client</span>
                        <span className="detail-value">
                            {bookingData.client.firstName} {bookingData.client.lastName}
                        </span>
                    </div>
                </div>
            </div>

            {isPaid && (
                <div className="confirmation-receipt">
                    <h3>Receipt</h3>
                    <div className="receipt-rows">
                        <div className="receipt-row">
                            <span>Session fee</span>
                            <span>${(bookingData.service.priceCents / 100).toFixed(2)}</span>
                        </div>
                        <div className="receipt-row receipt-total">
                            <span>Total charged</span>
                            <span>${(bookingData.service.priceCents / 100).toFixed(2)}</span>
                        </div>
                    </div>
                    <p className="receipt-note">
                        A receipt has been emailed to {bookingData.client.email}.
                    </p>
                </div>
            )}

            <div className="confirmation-actions">
                <a
                    href={generateCalendarLink()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                >
                    Add to calendar
                </a>
            </div>

            <div className="confirmation-next-steps">
                <h3>What happens next</h3>
                <ul>
                    <li>
                        <strong>Confirmation email.</strong> You&rsquo;ll receive a
                        confirmation at {bookingData.client.email} within a few minutes
                        {isPaid && ", along with your receipt"}.
                    </li>
                    <li>
                        <strong>Secure portal invitation.</strong> Within 24 hours,
                        you&rsquo;ll get a separate email inviting you to set up your
                        client portal account, where you&rsquo;ll complete the full
                        clinical intake.
                    </li>
                    <li>
                        <strong>Reminder.</strong> A reminder goes out 24 hours before
                        the session. If you need to reschedule, please do it at least
                        24 hours in advance to avoid the cancellation fee.
                    </li>
                    <li>
                        <strong>Questions before then?</strong> Reply to the
                        confirmation email or call (718) 555-0142.
                    </li>
                </ul>
            </div>

            <div className="confirmation-footer">
                <p>
                    Need to reschedule or cancel? Email{" "}
                    <a href="mailto:hello@harbortherapy.com">hello@harbortherapy.com</a>{" "}
                    or use the client portal once it&rsquo;s set up.
                </p>
            </div>
        </div>
    );
}

export default Confirmation;